/* eslint-disable react/prop-types */
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react'
import {
  CampaignReadyModal,
  DiscountModal,
  ExitFeedbackModal,
  PaymentSuccessModal,
} from '../../../../components/CampaignUpsellModals'
import {
  UPSELL_FLOWS,
  useUpsellCatalog,
  useUpgradePlansTab,
  useUpsellEventMap,
  useUpsellFlowState,
  useUpsellCheckout,
  useUpsellSuccessClose,
  trackUpsellExitFeedback,
  resolvePlanCheckoutPayload,
  resolvePackageCheckoutPayload,
} from '../UpsellFlows'
import {
  UPSELL_FEATURED_PACKAGE_CODE,
  UPSELL_FEATURED_PLAN_TIER,
  filterStaticPlanOptions,
  UPSELL_CAMPAIGN_PACKAGE_CODE,
} from '../UpsellFlows/upsellCatalog'
import {
  EXIT_FEEDBACK_OPTIONS,
  PLAN_OPTIONS,
  UPSELL_TABS,
} from '../UpsellFlows/sharedConstants'
import {
  ARTS_LIMIT_BONUS_OFFER_CODE,
  ARTS_LIMIT_UPSELL_STEPS,
  BONUS_OFFER,
  BONUS_PACKAGE,
  SMALLER_OFFER,
  SMALLER_PACKAGE,
} from './constants'

export const ArtsLimitUpsellFlow = forwardRef(function ArtsLimitUpsellFlow(
  { enabled = false, campaignId = null, onDismiss },
  ref,
) {
  const catalog = useUpsellCatalog({ enabled })
  const {
    openSubscriptionCheckout,
    openAddonCheckout,
  } = useUpsellCheckout()
  const {
    activeTab,
    setActiveTab,
    closeFlow: resetFlow,
    openFlow,
    isStep,
    goToStep,
    openSuccessStep,
    currentStep,
    isSuccessStepActive,
  } = useUpsellFlowState({
    enabled,
    initialStep: ARTS_LIMIT_UPSELL_STEPS.READY,
    initialTab: 'packages',
  })
  const upsellTabs = useUpgradePlansTab(catalog, activeTab, setActiveTab, UPSELL_TABS)

  useUpsellEventMap({
    flow: UPSELL_FLOWS.ARTS_LIMIT,
    campaignId,
    currentStep,
    activeTab,
  })

  const featuredPlanOffer = useMemo(
    () =>
      catalog.buildFeaturedPlanOffer() ?? {
        label: 'Plano Pro · 70 artes/mês',
        price: 'R$ 149,90/mês',
        unitPrice: 'R$ 2,14 por arte',
        description: 'Para quem cria com frequência. Cancele quando quiser.',
        name: 'Pro',
        artsPerMonth: 70,
      },
    [catalog],
  )

  const featuredPackageOffer = useMemo(
    () => catalog.buildFeaturedPackageOffer(catalog.featuredPackage),
    [catalog],
  )

  const packageOptions = useMemo(
    () => catalog.getPackageOptions([UPSELL_FEATURED_PACKAGE_CODE]),
    [catalog],
  )

  const featuredUpgradePlan = catalog.featuredUpgradePlan

  const planOptions = useMemo(() => {
    const featuredTier =
      catalog.featuredUpgradePlan?.tier ?? UPSELL_FEATURED_PLAN_TIER

    if (!catalog.dbPlans.length) {
      return filterStaticPlanOptions(
        PLAN_OPTIONS,
        catalog.currentPlanTier,
        featuredTier,
      )
    }

    return catalog.getUpgradePlanOptions()
  }, [catalog])

  const featuredPackageArts = featuredPackageOffer?.arts

  const hideFlow = resetFlow

  const closeFlow = useCallback(() => {
    resetFlow()
    onDismiss?.()
  }, [onDismiss, resetFlow])

  const closeSuccessFlow = useUpsellSuccessClose(closeFlow, isSuccessStepActive)

  const handleOpenPackageCheckout = useCallback((
    option,
    upsellSuccessStep = ARTS_LIMIT_UPSELL_STEPS.SUCCESS_PACKAGE,
  ) => {
    openAddonCheckout({
      ...resolvePackageCheckoutPayload(
        option,
        catalog.featuredPackage,
        UPSELL_FEATURED_PACKAGE_CODE,
      ),
      campaignArtGenerationId: campaignId,
      upsellFlow: UPSELL_FLOWS.ARTS_LIMIT,
      upsellSuccessStep,
      onOpen: hideFlow,
    })
  }, [campaignId, catalog.featuredPackage, hideFlow, openAddonCheckout])

  const handleOpenPlanCheckout = useCallback((option) => {
    openSubscriptionCheckout({
      ...resolvePlanCheckoutPayload(option, featuredUpgradePlan),
      campaignArtGenerationId: campaignId,
      upsellFlow: UPSELL_FLOWS.ARTS_LIMIT,
      upsellSuccessStep: ARTS_LIMIT_UPSELL_STEPS.SUCCESS_PACKAGE,
      onOpen: hideFlow,
    })
  }, [campaignId, featuredUpgradePlan, hideFlow, openSubscriptionCheckout])

  const handleOpenFeaturedPackageCheckout = useCallback(() => {
    handleOpenPackageCheckout(
      catalog.featuredPackage,
      ARTS_LIMIT_UPSELL_STEPS.SUCCESS_PACKAGE,
    )
  }, [catalog.featuredPackage, handleOpenPackageCheckout])

  const handleOpenBonusPackageCheckout = useCallback(() => {
    openAddonCheckout({
      ...resolvePackageCheckoutPayload(
        null,
        catalog.featuredPackage,
        UPSELL_FEATURED_PACKAGE_CODE,
      ),
      price: BONUS_OFFER.price,
      offerCode: ARTS_LIMIT_BONUS_OFFER_CODE,
      campaignArtGenerationId: campaignId,
      upsellFlow: UPSELL_FLOWS.ARTS_LIMIT,
      upsellSuccessStep: ARTS_LIMIT_UPSELL_STEPS.SUCCESS_BONUS,
      onOpen: hideFlow,
    })
  }, [campaignId, catalog.featuredPackage, hideFlow, openAddonCheckout])

  const handleOpenSmallerPackageCheckout = useCallback(() => {
    openAddonCheckout({
      ...resolvePackageCheckoutPayload(
        null,
        catalog.campaignPackage,
        UPSELL_CAMPAIGN_PACKAGE_CODE,
      ),
      price: SMALLER_OFFER.price,
      promotionCode: SMALLER_OFFER.couponCode,
      campaignArtGenerationId: campaignId,
      upsellFlow: UPSELL_FLOWS.ARTS_LIMIT,
      upsellSuccessStep: ARTS_LIMIT_UPSELL_STEPS.SUCCESS_SMALLER,
      onOpen: hideFlow,
    })
  }, [campaignId, catalog.campaignPackage, hideFlow, openAddonCheckout])

  useImperativeHandle(ref, () => ({
    open: openFlow,
    close: closeFlow,
    openSuccessStep,
  }), [closeFlow, openFlow, openSuccessStep])

  if (!enabled && !isSuccessStepActive) {
    return null
  }

  const packagesContent = {
    title: 'Suas artes acabaram — bem no embalo.',
    subtitle: 'Continue criando sem perder o ritmo.',
    marketLabel: '25 artes com um designer',
    marketPrice: 'a partir de R$ 1.250',
    platformLabel: 'No Xamã',
    platformPrice: featuredPackageOffer?.price,
    featuredOffer: featuredPackageOffer,
    otherOptionsTitle: 'Outras opções de pacotes avulsos',
    otherOptions: packageOptions,
    onSelectOption: handleOpenPackageCheckout,
    primaryAction: {
      label: 'Continuar criando',
      onClick: handleOpenFeaturedPackageCheckout,
    },
    tertiaryLink: catalog.hasSelectablePlans
      ? {
        label: 'Posta toda semana? No plano Pro, a arte sai por R$ 2,14 →',
        onClick: () => setActiveTab('plans'),
      }
      : undefined,
    skipAction: {
      label: 'Continuar sem gerar artes',
      onClick: () => goToStep(ARTS_LIMIT_UPSELL_STEPS.BONUS),
    },
  }

  const plansContent = {
    title: 'Suas artes acabaram — bem no embalo.',
    subtitle: 'Continue criando sem perder o ritmo.',
    marketLabel: 'Agência ou social media',
    marketPrice: 'R$ 2.000 a R$ 3.000 /mês',
    platformLabel: featuredPlanOffer.name
      ? `Plano ${featuredPlanOffer.name}`
      : 'Plano Pro',
    platformPrice: featuredPlanOffer.price,
    featuredOffer: featuredPlanOffer,
    otherOptionsTitle: 'Outras opções',
    otherOptions: planOptions,
    onSelectOption: handleOpenPlanCheckout,
    primaryAction: {
      label: 'Assinar e continuar criando',
      onClick: () => handleOpenPlanCheckout(featuredUpgradePlan),
    },
    tertiaryLink: {
      label: 'Precisa só de algumas artes pontuais? Veja os pacotes →',
      onClick: () => setActiveTab('packages'),
    },
    skipAction: {
      label: 'Continuar sem gerar artes',
      onClick: () => goToStep(ARTS_LIMIT_UPSELL_STEPS.BONUS),
    },
  }

  return (
    <>
      <CampaignReadyModal
        isOpen={isStep(ARTS_LIMIT_UPSELL_STEPS.READY)}
        onClose={() => goToStep(ARTS_LIMIT_UPSELL_STEPS.BONUS)}
        tabs={upsellTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        packages={packagesContent}
        plans={plansContent}
      />

      <DiscountModal
        isOpen={isStep(ARTS_LIMIT_UPSELL_STEPS.BONUS)}
        onClose={() => goToStep(ARTS_LIMIT_UPSELL_STEPS.SMALLER)}
        title="Leve 10 artes de bônus"
        subtitle="Só no seu primeiro pacote: 25 + 10 grátis."
        offer={BONUS_OFFER}
        primaryAction={{
          label: 'Pegar meu bônus',
          onClick: handleOpenBonusPackageCheckout,
        }}
        dismissAction={{
          label: 'agora não',
          onClick: () => goToStep(ARTS_LIMIT_UPSELL_STEPS.SMALLER),
        }}
      />

      <DiscountModal
        isOpen={isStep(ARTS_LIMIT_UPSELL_STEPS.SMALLER)}
        onClose={() => goToStep(ARTS_LIMIT_UPSELL_STEPS.EXIT)}
        title="Prefere começar com menos?"
        subtitle="10 artes para continuar a sua campanha."
        offer={SMALLER_OFFER}
        footnote="O cupom fica salvo na sua conta por 48h."
        primaryAction={{
          label: 'Continuar com 10 artes',
          onClick: handleOpenSmallerPackageCheckout,
        }}
        dismissAction={{
          label: 'Sair mesmo assim',
          onClick: () => goToStep(ARTS_LIMIT_UPSELL_STEPS.EXIT),
        }}
      />

      <ExitFeedbackModal
        isOpen={isStep(ARTS_LIMIT_UPSELL_STEPS.EXIT)}
        onClose={closeFlow}
        title="Beleza!"
        description="Tudo o que você criou fica salvo no histórico. Volte quando quiser continuar."
        feedback={{
          options: EXIT_FEEDBACK_OPTIONS,
        }}
        onExitWithFeedback={(reason) =>
          trackUpsellExitFeedback({
            flow: UPSELL_FLOWS.ARTS_LIMIT,
            campaignId,
            reasonId: reason.id,
            reasonLabel: reason.label,
          })
        }
        secondaryAction={{
          label: 'Voltar para a plataforma',
          onClick: closeFlow,
        }}
      />

      <PaymentSuccessModal
        isOpen={isStep(ARTS_LIMIT_UPSELL_STEPS.SUCCESS_PACKAGE)}
        onClose={closeSuccessFlow}
        title="Artes na conta!"
        description={`Suas ${featuredPackageArts} artes já estão disponíveis. Sua campanha ficou salva — continue exatamente de onde parou.`}
        primaryAction={{
          label: 'Voltar para minha campanha',
          onClick: closeSuccessFlow,
        }}
      />

      <PaymentSuccessModal
        isOpen={isStep(ARTS_LIMIT_UPSELL_STEPS.SUCCESS_BONUS)}
        onClose={closeSuccessFlow}
        title="Artes na conta!"
        description={`${BONUS_PACKAGE.totalArts} artes creditadas: ${BONUS_PACKAGE.packageArts} do pacote + ${BONUS_PACKAGE.bonusArts} de bônus. Continue exatamente de onde parou.`}
        primaryAction={{
          label: 'Voltar para minha campanha',
          onClick: closeSuccessFlow,
        }}
      />

      <PaymentSuccessModal
        isOpen={isStep(ARTS_LIMIT_UPSELL_STEPS.SUCCESS_SMALLER)}
        onClose={closeSuccessFlow}
        title="Artes na conta!"
        description={`${SMALLER_PACKAGE.artsCount} artes creditadas — você pagou ${SMALLER_OFFER.price} com o cupom ${SMALLER_OFFER.couponCode}. Continue de onde parou.`}
        primaryAction={{
          label: 'Voltar para minha campanha',
          onClick: closeSuccessFlow,
        }}
      />
    </>
  )
})

export default ArtsLimitUpsellFlow
